import {
  CompanySearchFilterNames,
  ContactSearchFilterNames,
  FILTER_TREE_TYPE,
  FilterTreeType,
  INVITED_EVENT_COUNT_MAX,
  SEARCH_FILTER_NAME,
  SearchFilterNameValueType,
  SOURCE_FILTER_TREE_NODE_ID
} from 'constants/filter';
import { PERSON_STATUS_CODE } from 'constants/person';
import { createArrayWithRange } from 'util/array';
import { escapeRegExp } from 'util/regex';
import { compareNumbers } from 'util/sort';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import { DiversityClassificationFilter } from '../../config/api';

//
// --- Types ---

export type AppliedFilterChipType = {
  /** true if has children and all are checked, false otherwise */
  allChildrenChecked: boolean;
  chipLabel?: string;
  filterTreeType: FilterTreeType | null;
  id: string;
  name: string;
  /** true if has children and one or more are checked, false if no children are checked */
  someChildrenChecked: boolean;
  tooltip?: string;
};

interface SearchResultFilterInterface<FilterName extends SearchFilterNameValueType, FilterDataType = {}> {
  /** function that determines if a record should be included or not */
  execute: (treeNodeData: any, filterData: FilterDataType) => boolean;
  /** data provided to the execute function to generate result */
  filterData?: FilterDataType;
  /** name of filter; should be unique in this application */
  name: FilterName;
  /** options for tree data provider */
  options: Partial<{
    closeNodeOnFilterMatch: boolean;
  }>;
}

type CompanyOrSupplier = {
  children: ReadonlyArray<CompanyOrSupplier>;
  isPerson: boolean;
};

type GetAppliedFilterChipForTreeNodeOptions = {
  parentNode?: TransformedFilterTreeNode;
  tooltip?: string;
};

type SearchResultActiveUserFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['ACTIVE_USER']>;
type SearchResultCountryOrStateFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['COUNTRY_OR_STATE']>;
type SearchResultDiversityClassificationFilterType = SearchResultFilterInterface<
  typeof SEARCH_FILTER_NAME['DIVERSITY_CLASSIFICATION']
>;
type SearchResultDiversityStatusFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['DIVERSITY_STATUS']>;
type SearchResultInvitedEventCountFilterType = SearchResultFilterInterface<
  typeof SEARCH_FILTER_NAME['INVITED_EVENT_COUNT']
>;
type SearchResultInvitedPortalCountFilterType = SearchResultFilterInterface<
  typeof SEARCH_FILTER_NAME['INVITED_PORTAL_COUNT']
>;
type SearchResultItemFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['ITEM']>;
type SearchResultNameFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['PERSON_NAME'], string>;
export type SearchResultParentCompanyPersonFilterType = SearchResultFilterInterface<
  typeof SEARCH_FILTER_NAME['PARENT_COMPANY_PERSON'],
  ReadonlyArray<string>
>;
type SearchResultPowerScoreFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['POWERSCORE'], number>;
type SearchResultSegmentationFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['SEGMENTATION']>;
type SearchResultSourceFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['SOURCE']>;
type SearchResultQualificationFilterType = SearchResultFilterInterface<typeof SEARCH_FILTER_NAME['QUALIFICATION']>;

export type SearchResultFilterType =
  | SearchResultActiveUserFilterType
  | SearchResultCountryOrStateFilterType
  | SearchResultDiversityClassificationFilterType
  | SearchResultDiversityStatusFilterType
  | SearchResultInvitedEventCountFilterType
  | SearchResultInvitedPortalCountFilterType
  | SearchResultItemFilterType
  | SearchResultNameFilterType
  | SearchResultParentCompanyPersonFilterType
  | SearchResultPowerScoreFilterType
  | SearchResultQualificationFilterType
  | SearchResultSegmentationFilterType
  | SearchResultSourceFilterType;

//
//  --- Search Filters ---

/**
 * Search filter object that filters tree nodes by person name
 */
export const searchResultActiveUserFilter: SearchResultActiveUserFilterType = {
  execute: treeNodeData => {
    if (treeNodeData.isPerson) {
      const isStatusCodeActive = treeNodeData.personStatusCode === PERSON_STATUS_CODE.ACTIVE;
      const isLastLoginWithinLastYear = (() => {
        if (treeNodeData.lastLoginSuccessTimestamp) {
          const oneYearAgoDate = new Date();
          oneYearAgoDate.setUTCFullYear(oneYearAgoDate.getUTCFullYear() - 1);
          oneYearAgoDate.setUTCHours(0, 0, 0, 0);
          return treeNodeData.lastLoginSuccessTimestamp
            ? treeNodeData.lastLoginSuccessTimestamp > oneYearAgoDate.getTime()
            : false;
        }
        return false;
      })();
      return isStatusCodeActive && isLastLoginWithinLastYear;
    }

    return false;
  },
  name: SEARCH_FILTER_NAME.ACTIVE_USER,
  options: {
    closeNodeOnFilterMatch: false
  }
};

/**
 * Search filter object that filters tree nodes by country or state
 */
export const searchResultCountryOrStateFilter: SearchResultCountryOrStateFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.COUNTRY_OR_STATE,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by diversity classification status
 */
export const searchResultDiversityClassificationFilter: SearchResultDiversityClassificationFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.DIVERSITY_CLASSIFICATION,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by diversity status
 */
export const searchResultDiversityStatusFilter: SearchResultDiversityStatusFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.DIVERSITY_STATUS,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by number of bid events invited
 */
export const searchResultInvitedEventCountFilter: SearchResultInvitedEventCountFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.INVITED_EVENT_COUNT,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by number of portals invited
 */
export const searchResultInvitedPortalCountFilter: SearchResultInvitedPortalCountFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.INVITED_PORTAL_COUNT,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by item
 */
export const searchResultItemFilter: SearchResultItemFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.ITEM,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by segmentation status
 */
export const searchResultSegmentationFilter: SearchResultSegmentationFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.SEGMENTATION,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by source status
 */
export const searchResultSourceFilter: SearchResultSourceFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.SOURCE,
  options: { closeNodeOnFilterMatch: true }
};

/**
 * Search filter object that filters tree nodes by person name
 */
export const searchResultNameFilter: SearchResultNameFilterType = {
  execute: (treeNodeData, filterData) => {
    if (treeNodeData.isPerson) {
      const searchTermRegex = new RegExp(escapeRegExp(filterData), 'i');
      return !!treeNodeData.name.match(searchTermRegex);
    }
    return false;
  },
  name: SEARCH_FILTER_NAME.PERSON_NAME,
  options: {
    closeNodeOnFilterMatch: false
  }
};

export const searchResultQualificationFilter: SearchResultQualificationFilterType = {
  execute: treeNodeData => executeDiscoveryFilter(treeNodeData),
  name: SEARCH_FILTER_NAME.QUALIFICATION,
  options: { closeNodeOnFilterMatch: true }
};

export const treeTypeToSearchFilter: Record<FilterTreeType, SearchResultFilterType> = {
  [FILTER_TREE_TYPE.CONTACT]: searchResultNameFilter,
  [FILTER_TREE_TYPE.COUNTRY_OR_STATE]: searchResultCountryOrStateFilter,
  [FILTER_TREE_TYPE.DIVERSE]: searchResultDiversityStatusFilter,
  [FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]: searchResultDiversityClassificationFilter,
  [FILTER_TREE_TYPE.INVITED_EVENT_COUNT]: searchResultInvitedEventCountFilter,
  [FILTER_TREE_TYPE.INVITED_PORTAL_COUNT]: searchResultInvitedPortalCountFilter,
  [FILTER_TREE_TYPE.ITEM]: searchResultItemFilter,
  [FILTER_TREE_TYPE.QUALIFICATION]: searchResultQualificationFilter,
  [FILTER_TREE_TYPE.SEGMENTATION]: searchResultSegmentationFilter,
  [FILTER_TREE_TYPE.SOURCE]: searchResultSourceFilter
};

/**
 * Search filter for showing/hiding people in parent companies
 */

export const searchResultParentCompanyPersonFilter: SearchResultParentCompanyPersonFilterType = {
  execute: (treeNodeData, filterData) => {
    // if we have a supplier node, a (person) node that does not have supplier siblings, or a (person) node
    // that has a supplier id in the filterData exclusion array, return true
    if (
      treeNodeData.isSupplier ||
      !treeNodeData.hasSupplierSibling ||
      treeNodeData.createCompanyNode ||
      filterData.includes(treeNodeData.supplierId)
    ) {
      return true;
    }

    // only return first person
    return treeNodeData.supplierPersonIndex === 0;
  },
  name: SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON,
  options: {
    closeNodeOnFilterMatch: false
  }
};

export const searchResultPowerScoreFilter: SearchResultPowerScoreFilterType = {
  execute: (treeNodeData, filterData) => {
    // use 1 as default minimumPowerScore if valid filterData not set or not a number greater than 0
    const minimumPowerScore = typeof filterData === 'number' && filterData > 0 ? filterData : 1;
    if (treeNodeData.isPerson) {
      return treeNodeData.powerScore >= minimumPowerScore;
    }
    return false;
  },
  name: SEARCH_FILTER_NAME.POWERSCORE,
  options: {
    closeNodeOnFilterMatch: false
  }
};

/**
 * Function for manually applying the current search filters on the current search results
 * so that the filtered data hidden by the tree data provider is now available to be used.
 */
export function getFilteredSearchResults(
  searchResults: ReadonlyArray<CompanyOrSupplier>,
  searchFilters: readonly SearchResultFilterType[]
) {
  if (!Array.isArray(searchFilters) || searchFilters.length <= 0) return searchResults;
  if (!Array.isArray(searchResults) || searchResults.length <= 0) return [];

  const modifiedSearchFilters: SearchResultFilterType[] = searchFilters.filter(
    searchFilter => searchFilter.name !== SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON
  );
  const companyFilters: SearchResultFilterType[] = modifiedSearchFilters.filter(searchFilter =>
    CompanySearchFilterNames.includes(searchFilter.name)
  );
  const contactFilters: SearchResultFilterType[] = modifiedSearchFilters.filter(searchFilter =>
    ContactSearchFilterNames.includes(searchFilter.name)
  );

  // for a person, apply all filters
  const personSearchFilter = (treeNodeData: any) => {
    return modifiedSearchFilters.every((filter: any) => filter.execute(treeNodeData, filter.filterData));
  };
  // for a company, there are different cases for combinations of contact and company filters
  const companySearchFilter = (treeNodeData: any) => {
    const applyCompanyFilters = companyFilters.every((filter: any) => filter.execute(treeNodeData, filter.filterData));
    // if there are no company filters and the company has no children, it should filter out
    // if there are only company filters, apply them
    // if there are both contact and company filters, apply company filters and filter it out if there are no children
    return companyFilters.length === 0
      ? treeNodeData.children.length > 0
      : contactFilters.length === 0
      ? applyCompanyFilters
      : applyCompanyFilters && treeNodeData.children.length > 0;
  };

  const recursiveApplyFilters = (node: any) => {
    return {
      ...node,
      children: node.children
        .map((child: any) => (child.isPerson ? child : recursiveApplyFilters(child)))
        .filter((child: any) => (child.isPerson ? personSearchFilter(child) : companySearchFilter(child)))
    };
  };

  return modifiedSearchFilters.length === 0
    ? searchResults
    : searchResults.map((company: any) => recursiveApplyFilters(company)).filter(companySearchFilter);
}

/**
 * A function for recursively filtering tree nodes based on if they have the matchesDiscoveryFilters
 * @param treeNodeData
 */
const executeDiscoveryFilter = (treeNodeData: any) => {
  function recursiveDiversityStatusFilter(treeNodeData: any) {
    if (treeNodeData.children) {
      return treeNodeData.matchesDiscoveryFilters || treeNodeData.children.some(recursiveDiversityStatusFilter);
    }
    if (treeNodeData.isPerson) {
      return treeNodeData.matchesDiscoveryFilters;
    }
    return false;
  }
  return !!recursiveDiversityStatusFilter(treeNodeData);
};

//
// --- Filter Tree ---

/**
 * Returns true if all children of a given tree node are checked. Returns false is not all
 * children are checked, or if node does not have any children
 */
export function areAllFilterTreeNodeChildrenChecked(
  treeNode: TransformedFilterTreeNode,
  checkedFilterIds: ReadonlyArray<string>
) {
  if (
    !treeNode?.children ||
    !Array.isArray(treeNode.children) ||
    !treeNode.children.length ||
    !Array.isArray(checkedFilterIds) ||
    !checkedFilterIds.length
  ) {
    return false;
  }

  return treeNode.children.every(childNode => checkedFilterIds.includes(childNode.id));
}

export function areSomeFilterTreeNodeChildrenChecked(
  treeNode: TransformedFilterTreeNode,
  checkedFilterIds: ReadonlyArray<string>
): boolean {
  const hasChildren = treeNode?.children && Array.isArray(treeNode.children) && treeNode.children.length;
  const hasCheckedFilterIds = Array.isArray(checkedFilterIds) && checkedFilterIds.length > 0;

  if (!hasChildren || !hasCheckedFilterIds) {
    return false;
  }

  return treeNode.children.some(childNode =>
    Array.isArray(childNode.children) && childNode.children.length > 0
      ? areSomeFilterTreeNodeChildrenChecked(childNode, checkedFilterIds)
      : checkedFilterIds.includes(childNode.id)
  );
}

/**
 * Takes in an array of transformed nodes and returns a copy of the tree with node checked
 * properties set, depending on filter ids provided
 *
 * @returns an new array of tree nodes
 */
export function generateFilterTreeNodesWithCheckedNodes(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  checkedFilterIds: ReadonlyArray<string>
): ReadonlyArray<TransformedFilterTreeNode> {
  if (!Array.isArray(checkedFilterIds)) {
    return treeNodes;
  }

  return treeNodes.map(treeNode => {
    const checkedValue = treeNode.checked !== null ? checkedFilterIds.includes(treeNode.id) : null;

    return {
      ...treeNode,
      checked: checkedValue,
      childChecked: areSomeFilterTreeNodeChildrenChecked(treeNode, checkedFilterIds),
      children:
        Array.isArray(treeNode.children) && treeNode.children.length
          ? generateFilterTreeNodesWithCheckedNodes(treeNode.children, checkedFilterIds)
          : treeNode.children
    };
  });
}

/**
 * Given an array of tree nodes and list of open node ids, this will generate a copy of tree
 * data with open property set to open for nodes matching provided ids. This will recursively
 * scan node children
 */
export function generateFilterTreeNodesWithOpenNodes(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  openTreeNodeIds: ReadonlyArray<string>
): ReadonlyArray<TransformedFilterTreeNode> {
  if (!Array.isArray(openTreeNodeIds)) {
    return treeNodes;
  }

  return treeNodes.map(treeNode => ({
    ...treeNode,
    children:
      Array.isArray(treeNode.children) && treeNode.children.length
        ? generateFilterTreeNodesWithOpenNodes(treeNode.children, openTreeNodeIds)
        : treeNode.children,
    open: openTreeNodeIds.includes(treeNode.id)
  }));
}

/**
 * Get an array of node ids for all children of a given tree node
 */
export function getAllTreeNodeChildrenIds(treeNode: TransformedFilterTreeNode): ReadonlyArray<string> {
  return getAllTreeNodeIds([treeNode]).filter(treeNodeId => treeNodeId !== treeNode.id);
}

/**
 * Get an array of all node ids currently in a filter tree node list
 */
export function getAllTreeNodeIds(treeNodes: ReadonlyArray<TransformedFilterTreeNode>): ReadonlyArray<string> {
  if (!Array.isArray(treeNodes) || !treeNodes.length) {
    return [];
  }

  return treeNodes.reduce((treeNodeIds, currentTreeNode) => {
    if (!currentTreeNode || !currentTreeNode.id) {
      return treeNodeIds;
    } else if (Array.isArray(currentTreeNode.children) && currentTreeNode.children.length) {
      return treeNodeIds.concat([currentTreeNode.id], ...getAllTreeNodeIds(currentTreeNode.children));
    } else {
      return [...treeNodeIds, currentTreeNode.id];
    }
  }, []);
}

/**
 * Given a filter tree node, return an AppliedFilterChipType object
 *
 * NOTE: allChildrenChecked and someChildrenChecked need to be updated outside this function
 */
export function getAppliedFilterChipForTreeNode(
  treeNode: TransformedFilterTreeNode,
  opts: GetAppliedFilterChipForTreeNodeOptions = {}
): AppliedFilterChipType {
  const { tooltip } = opts;

  return {
    allChildrenChecked: false,
    filterTreeType: treeNode.filterTreeType,
    id: treeNode.id || '',
    name: treeNode.filterChipLabel || treeNode.name || '',
    someChildrenChecked: false,
    tooltip
  };
}

/**
 * Scan list of applied filter chips and updates the allChildrenChecked and
 * someChildrenChecked properties
 */
export function getAppliedFilterChipsWithUpdatedChildrenStatus(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  appliedFilterChips: ReadonlyArray<AppliedFilterChipType>,
  checkedFilterIds: ReadonlyArray<string>
) {
  if (
    !Array.isArray(treeNodes) ||
    !Array.isArray(appliedFilterChips) ||
    !Array.isArray(checkedFilterIds) ||
    !treeNodes.length ||
    !appliedFilterChips.length ||
    !checkedFilterIds.length
  ) {
    return [];
  }

  return appliedFilterChips.map(appliedFilter => {
    const treeNode = getFilterNodeById(treeNodes, appliedFilter.id);
    const treeNodeHasChildren =
      !!treeNode?.children && Array.isArray(treeNode.children) && treeNode.children.length > 0;

    const childrenTreeNodeIds = treeNode && treeNodeHasChildren ? getAllTreeNodeChildrenIds(treeNode) : [];

    // if node does not have children, or we don't have id
    if (!treeNodeHasChildren || !childrenTreeNodeIds.length) {
      return { ...appliedFilter, allChildrenChecked: false, someChildrenChecked: false };
    }

    return {
      ...appliedFilter,
      allChildrenChecked: childrenTreeNodeIds.every(treeNodeId => checkedFilterIds.includes(treeNodeId)),
      someChildrenChecked: childrenTreeNodeIds.some(treeNodeId => checkedFilterIds.includes(treeNodeId))
    };
  });
}

/**
 * Takes in the current list of checked filter ids, and returns a new array
 * that includes ids for node and children
 */
export function getCheckedFilterIdsAfterNodeChecked(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  checkedTreeNode: TransformedFilterTreeNode,
  currentCheckedFilterIds: ReadonlyArray<string>
): ReadonlyArray<string> {
  if (!Array.isArray(treeNodes) || !treeNodes.length || !Array.isArray(currentCheckedFilterIds)) {
    return [];
  }

  // add ids for children as well, this does include checkedTreeNodes id as well, not only ids of the the children nodes
  const allTreeNodeIds = getAllTreeNodeIds([checkedTreeNode]);
  let newCheckedFilterIds = [...currentCheckedFilterIds, ...allTreeNodeIds];

  if (checkedTreeNode.parentId && !newCheckedFilterIds.includes(checkedTreeNode.parentId)) {
    const parentNode = getFilterParentNode(treeNodes, checkedTreeNode);
    if (parentNode) {
      const allParentNodeChildrenIds = getAllTreeNodeIds([parentNode]).filter(
        treeNodeId => treeNodeId !== parentNode.id
      );
      const allChildrenChecked = allParentNodeChildrenIds.every(treeNodeId => newCheckedFilterIds.includes(treeNodeId));
      // don't add root nodes
      if (allChildrenChecked && parentNode.checked !== null) {
        newCheckedFilterIds = [...newCheckedFilterIds, parentNode.id];
      }
    }
  }

  // now return unique values only
  return Array.from(new Set<string>(newCheckedFilterIds));
}

/**
 * Takes in the current list of checked filter ids, and returns a new array
 * that excludes ids for node and children
 */
export function getCheckedFilterIdsAfterNodeUnchecked(
  currentCheckedFilterIds: ReadonlyArray<string>,
  uncheckedTreeNode: TransformedFilterTreeNode
): ReadonlyArray<string> {
  // this does include treeNodes id as well, not just ids of the the children nodes
  const allTreeNodeIds = getAllTreeNodeIds([uncheckedTreeNode]);
  return currentCheckedFilterIds.filter(filterId => !allTreeNodeIds.includes(filterId));
}

/**
 * Finds a specific node by node id in an array of tree nodes (including nested children)
 */
export function getFilterNodeById(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  treeNodeId: string | null
): TransformedFilterTreeNode | undefined {
  if (!treeNodeId) {
    return undefined;
  }

  const flatTreeNodesList = getFlattenedTreeNodeList(treeNodes);
  return flatTreeNodesList.find(treeNode => treeNode.id === treeNodeId);
}

/**
 * get parent node of a given tree node
 */
export function getFilterParentNode(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  treeNode: TransformedFilterTreeNode
): TransformedFilterTreeNode | undefined {
  if (!Array.isArray(treeNodes) || !treeNodes.length || !treeNode?.parentId) {
    return undefined;
  }

  const parentNode = getFilterNodeById(treeNodes, treeNode.parentId);
  if (!parentNode) {
    return undefined;
  }

  // verify tree node is actually a parent of the parent node we found
  const allParentNodeChildrenNodeIds = getAllTreeNodeIds([parentNode]);
  return parentNode && allParentNodeChildrenNodeIds.includes(treeNode.id) ? parentNode : undefined;
}

/**
 * get node ids for all ancestor nodes
 */
export function getFilterParentNodeIds(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  treeNodeId: string
): ReadonlyArray<string> {
  if (!Array.isArray(treeNodes) || !treeNodes.length) {
    return [];
  }

  const treeNode = getFilterNodeById(treeNodes, treeNodeId);

  if (!treeNode || !treeNode.parentId) {
    return [];
  }

  const parentNodeIds = [];
  let parentNode = getFilterParentNode(treeNodes, treeNode);

  while (parentNode) {
    parentNodeIds.push(parentNode.id);
    parentNode = getFilterParentNode(treeNodes, parentNode);
  }

  return parentNodeIds;
}

/**
 * Flattens array of tree nodes, including nested children nodes, into a single-level
 * flat array of all nodes
 */
export function getFlattenedTreeNodeList(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>
): ReadonlyArray<TransformedFilterTreeNode> {
  if (!Array.isArray(treeNodes) || !treeNodes.length) {
    return [];
  }

  return treeNodes.reduce<ReadonlyArray<TransformedFilterTreeNode>>((flatTreeNodes, currentTreeNode) => {
    if (Array.isArray(currentTreeNode.children) && currentTreeNode.children.length) {
      return flatTreeNodes.concat([currentTreeNode], ...getFlattenedTreeNodeList(currentTreeNode.children));
    } else {
      return flatTreeNodes.concat([currentTreeNode]);
    }
  }, []);
}

/**
 * Checks list of applied filters for parent/child relationships. When all
 * children are checked, remove the matching applied filter objects for the children, and add
 * an applied filter for the parent instead.
 */
export function getRolledUpAppliedFilters(
  treeNodes: ReadonlyArray<TransformedFilterTreeNode>,
  appliedFilterChips: ReadonlyArray<AppliedFilterChipType>,
  checkedFilterIds: ReadonlyArray<string>
): ReadonlyArray<AppliedFilterChipType> {
  if (
    !Array.isArray(treeNodes) ||
    !Array.isArray(appliedFilterChips) ||
    !Array.isArray(checkedFilterIds) ||
    !treeNodes.length ||
    !appliedFilterChips.length ||
    !checkedFilterIds.length
  ) {
    return [];
  }

  const appliedFilterIds = appliedFilterChips.map(appliedFilter => appliedFilter.id);

  // internally keep track of removed nodes so they can be skipped if necessary
  let appliedFilterIdsToRemove: ReadonlyArray<string> = [];

  const updatedAppliedFilters = appliedFilterChips.reduce<ReadonlyArray<AppliedFilterChipType>>(
    (newAppliedFilters, currentAppliedFilter) => {
      // if filter in filters to remove list, skip adding to newAppliedFilters
      if (appliedFilterIdsToRemove.includes(currentAppliedFilter.id)) {
        return newAppliedFilters;
      }

      // see if parent node is available
      const treeNode = getFilterNodeById(treeNodes, currentAppliedFilter.id);
      const parentTreeNode = treeNode ? getFilterParentNode(treeNodes, treeNode) : null;

      // if no parent node, or parent node is not checkable (null), add this current filter back to the new list
      if (!parentTreeNode || parentTreeNode.checked === null) {
        return [...newAppliedFilters, currentAppliedFilter];
      }

      // see if all parents children are checked
      if (areAllFilterTreeNodeChildrenChecked(parentTreeNode, checkedFilterIds)) {
        const allParentNodeChildrenIds = getAllTreeNodeChildrenIds(parentTreeNode);
        appliedFilterIdsToRemove = appliedFilterIdsToRemove.concat(allParentNodeChildrenIds);

        // if parent node does not already exist, add it
        if (!appliedFilterIds.includes(parentTreeNode.id)) {
          const parentsParentNode = getFilterParentNode(treeNodes, parentTreeNode);
          return parentsParentNode
            ? [...newAppliedFilters, getAppliedFilterChipForTreeNode(parentTreeNode, parentsParentNode)]
            : newAppliedFilters;
        }
      }

      return [...newAppliedFilters, currentAppliedFilter];
    },
    []
  );

  // do one more pass to make sure all applied filters set for removal have been removed
  return updatedAppliedFilters.filter(appliedFilter => !appliedFilterIdsToRemove.includes(appliedFilter.id));
}

//
// --- Invited Event Filter Util Functions ---

export function getSummaryTextForSupplierActivityCountFilterRange(rangeStart: number, rangeEnd: number): string {
  if (rangeEnd === INVITED_EVENT_COUNT_MAX) {
    return `${rangeStart}+`;
  }

  if (rangeStart === rangeEnd) {
    return rangeStart === 0 ? 'No' : `${rangeEnd}`;
  }

  return `${rangeStart}-${rangeEnd}`;
}

export function getSupplierActivityCountFiltersFromRange(rangeStart: number, rangeEnd: number): ReadonlyArray<number> {
  if ((rangeStart !== 0 && !rangeStart) || (rangeEnd !== 0 && !rangeEnd)) {
    return [];
  }

  if (rangeStart === 0 && rangeEnd === INVITED_EVENT_COUNT_MAX) {
    return [];
  }

  if (rangeStart === rangeEnd) {
    return [rangeStart];
  }

  return [...createArrayWithRange(rangeStart, rangeEnd)].sort(compareNumbers);
}

export function generateDiverstiyClassificationFilters(
  checkedDivClassFilters: ReadonlyArray<string>,
  filterTree: ReadonlyArray<TransformedFilterTreeNode>
): ReadonlyArray<DiversityClassificationFilter> {
  const flatTreeNodesList = getFlattenedTreeNodeList(filterTree);
  const treeNodes = checkedDivClassFilters
    .map(id => {
      return flatTreeNodesList.find(treeNode => treeNode.id === id);
    })
    .filter((treeNode): treeNode is TransformedFilterTreeNode => !!treeNode);

  return Array.from(treeNodes, treeNode =>
    treeNode.fieldMap
      ? {
          id: treeNode.id,
          isWoodMacFilter: treeNode.fieldMap['isWoodMacFilter']
        }
      : { id: treeNode.id }
  );
}

//
// --- Matches Discovery Filters Functions ---

export function addMatchesDiscoveryFiltersToEipCompanyList(
  eipCompanyList: ReadonlyArray<any>,
  eipCompaniesMatchesDiscoveryFiltersMap: { [companyKey: number]: boolean }
): ReadonlyArray<any> {
  if (!Array.isArray(eipCompanyList) || eipCompanyList.length === 0) return [];

  return eipCompanyList.map(company => {
    // if this node is a supplier, just apply to children
    if (!company.eipCompanyKey) {
      return {
        ...company,
        children: addMatchesDiscoveryFiltersToEipCompanyList(company.children, eipCompaniesMatchesDiscoveryFiltersMap)
      };
    } else {
      const matchesDiscoveryFilters = eipCompaniesMatchesDiscoveryFiltersMap[company.eipCompanyKey] || false;
      return {
        ...company,
        children: company.children.map((person: any) => ({ ...person, matchesDiscoveryFilters })),
        matchesDiscoveryFilters
      };
    }
  });
}

export function addMatchesDiscoveryFiltersToFrequentContactList(
  frequentContactsList: ReadonlyArray<any>,
  frequentContactsMatchesDiscoveryFiltersMap: { [supplierId: string]: boolean }
) {
  if (!Array.isArray(frequentContactsList) || frequentContactsList.length === 0) return [];

  return frequentContactsList
    .map(topLevelSupplier =>
      addMatchesDiscoveryFiltersToSupplierTree(topLevelSupplier, frequentContactsMatchesDiscoveryFiltersMap)
    )
    .filter(topLevelSupplier => topLevelSupplier.children.length > 0);
}

// Add the correct matches discovery filters boolean to one supplier family tree.
function addMatchesDiscoveryFiltersToSupplierTree(
  supplier: { id: number; children: ReadonlyArray<any> },
  supplierIdMatchesDiscoveryFiltersMap: { [supplierId: string]: boolean }
): any {
  const matchesDiscoveryFilters = supplierIdMatchesDiscoveryFiltersMap[supplier.id] || false;
  return {
    ...supplier,
    matchesDiscoveryFilters,
    children: supplier.children.map(child =>
      child.isSupplier
        ? addMatchesDiscoveryFiltersToSupplierTree(child, supplierIdMatchesDiscoveryFiltersMap)
        : { ...child, matchesDiscoveryFilters }
    )
  };
}

//
// --- Filter API Helpers ---

export function isRegisteredSupplierFilterEnabled(selectedSourceFilterIds: ReadonlyArray<string>): boolean {
  if (!Array.isArray(selectedSourceFilterIds) || !selectedSourceFilterIds.length) {
    return false;
  }

  return selectedSourceFilterIds.includes(SOURCE_FILTER_TREE_NODE_ID.REGISTERED_SUPPLIER);
}
